import React, { useState, useEffect } from 'react';
import {
  Brain,
  DollarSign,
  AlertCircle,
  Settings,
  TrendingUp,
  Activity,
  Shield,
  Check,
  X,
  RefreshCw,
  Save,
  Key,
  Info,
  AlertTriangle,
  Zap,
  BarChart3,
  MessageSquare,
  Mail,
  FileText,
  Image,
  ChevronRight,
  ExternalLink,
  TestTube,
  Globe,
  Sparkles,
  Code,
  Database,
  Calculator
} from 'lucide-react';
import { supabase } from '../config/supabase';
import '../styles/animations.css';

interface Provider {
  id: string;
  name: string;
  models: Model[];
  requiresKey: boolean;
  keyFormat: string;
  website: string;
}

interface Model {
  id: string;
  name: string;
  costPer1k: number;
  description: string;
}

interface ModuleConfig {
  moduleName: string;
  displayName: string;
  description: string;
  icon: React.ElementType;
  currentProvider: string;
  currentModel: string;
  apiKey: string;
  monthlyLimit: number;
  currentUsage: number;
  isEnabled: boolean;
  hasKey?: boolean;
}

const APISettingsPage = () => {
  const [modules, setModules] = useState<ModuleConfig[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'modules' | 'usage' | 'providers'>('modules');
  const [editingModule, setEditingModule] = useState<ModuleConfig | null>(null);
  const [usageStats, setUsageStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [syncingData, setSyncingData] = useState(false);

  // API URL - używamy backendu
  const API_URL = import.meta.env.PROD 
    ? 'https://api.plexisystem.pl/api'
    : 'http://localhost:3001/api';

  // Lista modułów w systemie z sugerowanymi modelami
  const systemModules = [
    {
      moduleName: 'pdf_generator',
      displayName: 'Generator Ofert PDF',
      description: 'Generowanie treści ofert i dokumentów',
      icon: FileText
    },
    {
      moduleName: 'google_ads',
      displayName: 'Google Ads Manager',
      description: 'Analiza kampanii i optymalizacje',
      icon: TrendingUp,
      suggestedModel: 'claude-3-haiku-20240307' // TANI model dla optymalizacji
    },
    {
      moduleName: 'chatbot',
      displayName: 'Chatbot',
      description: 'Rozmowy z klientami',
      icon: MessageSquare
    },
    {
      moduleName: 'social_media',
      displayName: 'Social Media',
      description: 'Generowanie postów',
      icon: Image,
      suggestedModel: 'claude-3-haiku-20240307' // TANI model wystarczy
    },
    {
      moduleName: 'email_marketing',
      displayName: 'Email Marketing',
      description: 'Tworzenie kampanii email',
      icon: Mail
    },
    {
      moduleName: 'analytics',
      displayName: 'Analityka',
      description: 'Generowanie raportów',
      icon: BarChart3
    },
    {
      moduleName: 'ai_assistant',
      displayName: 'Asystent AI',
      description: 'Ogólne wsparcie AI',
      icon: Brain
    },
    {
      moduleName: 'product_description',
      displayName: 'Opisy Produktów',
      description: 'Generowanie opisów i specyfikacji',
      icon: FileText
    },
    {
      moduleName: 'calculator',
      displayName: 'Kalkulator Inteligentny',
      description: 'Wyceny z AI',
      icon: Calculator
    },
    {
      moduleName: 'code_assistant',
      displayName: 'Asystent Kodu',
      description: 'Pomoc w programowaniu',
      icon: Code
    },
    {
      moduleName: 'telegram_bot',
      displayName: 'Telegram Bot',
      description: 'Asystent AI w Telegram',
      icon: MessageSquare
    },
    {
      moduleName: 'automation',
      displayName: 'Automatyzacja AI',
      description: 'Automatyczne generowanie postów i optymalizacja kampanii',
      icon: Zap,
      suggestedModel: 'claude-3-haiku-20240307' // TANI model dla automatyzacji!
    }
  ];

  // Pobierz listę dostawców
  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API_URL}/universal-ai/providers`);
      if (response.ok) {
        const data = await response.json();
        // Data może być tablicą lub obiektem z polem providers
        setProviders(Array.isArray(data) ? data : data.providers || []);
      } else {
        console.error('Failed to fetch providers');
        // Użyj domyślnych providerów
        setProviders([
          {
            id: 'openai',
            name: 'OpenAI',
            models: [
              { id: 'gpt-4', name: 'GPT-4', costPer1k: 0.03, description: 'Najlepszy model' },
              { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', costPer1k: 0.002, description: 'Szybki i tani' }
            ],
            requiresKey: true,
            keyFormat: 'sk-...',
            website: 'https://platform.openai.com'
          },
          {
            id: 'anthropic',
            name: 'Anthropic',
            models: [
              { id: 'claude-3-opus', name: 'Claude 3 Opus', costPer1k: 0.075, description: 'Najlepszy Claude' },
              { id: 'claude-3-sonnet', name: 'Claude 3 Sonnet', costPer1k: 0.025, description: 'Balans wydajności' }
            ],
            requiresKey: true,
            keyFormat: 'sk-ant-...',
            website: 'https://console.anthropic.com'
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      // Użyj domyślnych providerów w przypadku błędu
      setProviders([
        {
          id: 'openai',
          name: 'OpenAI',
          models: [
            { id: 'gpt-4', name: 'GPT-4', costPer1k: 0.03, description: 'Najlepszy model' },
            { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', costPer1k: 0.002, description: 'Szybki i tani' }
          ],
          requiresKey: true,
          keyFormat: 'sk-...',
          website: 'https://platform.openai.com'
        }
      ]);
    }
  };

  // Pobierz ustawienia modułów
  const fetchModules = async () => {
    setIsLoading(true);
    try {
      const moduleConfigs = await Promise.all(
        systemModules.map(async (sysModule) => {
          try {
            const response = await fetch(`${API_URL}/universal-ai/settings/${sysModule.moduleName}`);
            const settings = await response.json();
            
            return {
              ...sysModule,
              currentProvider: settings.api_provider || 'none',
              currentModel: settings.model_name || '',
              apiKey: settings.api_key || '',
              monthlyLimit: settings.monthly_limit || 100,
              currentUsage: settings.current_usage || 0,
              isEnabled: settings.enabled || false,
              hasKey: settings.has_key || false
            };
          } catch (error) {
            console.error(`Error fetching settings for ${sysModule.moduleName}:`, error);
            return {
              ...sysModule,
              currentProvider: 'none',
              currentModel: '',
              apiKey: '',
              monthlyLimit: 100,
              currentUsage: 0,
              isEnabled: false,
              hasKey: false
            };
          }
        })
      );
      
      setModules(moduleConfigs);
    } catch (error) {
      console.error('Error fetching modules:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Zapisz ustawienia modułu
  const saveModuleSettings = async (module: ModuleConfig) => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/universal-ai/settings/${module.moduleName}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_provider: module.currentProvider,
          model_name: module.currentModel,
          api_key: module.apiKey,
          monthly_limit: module.monthlyLimit,
          enabled: module.isEnabled
        })
      });

      if (response.ok) {
        // Jeśli zapisujemy moduł automation, zapisz klucz również w localStorage
        if (module.moduleName === 'automation' && module.apiKey && !module.apiKey.startsWith('***')) {
          localStorage.setItem('anthropic_api_key', module.apiKey);
        }
        
        // Jeśli zapisujemy moduł telegram_bot, zapisz też do telegram_ai_settings
        if (module.moduleName === 'telegram_bot') {
          try {
            const { supabase } = await import('../config/supabase');
            await supabase
              .from('telegram_ai_settings')
              .update({
                api_key: module.apiKey,
                model: module.currentModel,
                updated_at: new Date().toISOString()
              })
              .eq('id', '65cd161a-c11f-44e7-bbcf-0712c687e8f9');
          } catch (error) {
            console.error('Error updating telegram_ai_settings:', error);
          }
        }
        alert('✅ Ustawienia zapisane pomyślnie!');
        setEditingModule(null);
        fetchModules(); // Odśwież dane
      } else {
        throw new Error('Błąd zapisu');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('❌ Błąd podczas zapisywania ustawień');
    } finally {
      setIsSaving(false);
    }
  };

  // Testuj połączenie z API
  const testAPIConnection = async (module: ModuleConfig) => {
    if (!module.apiKey || module.apiKey.startsWith('***')) {
      alert('❌ Wprowadź klucz API');
      return;
    }

    setTestingConnection(module.moduleName);
    
    try {
      const response = await fetch(`${API_URL}/universal-ai/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: module.currentProvider,
          api_key: module.apiKey,
          model: module.currentModel
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(`✅ Połączenie działa!\n${result.message}`);
      } else {
        alert(`❌ Błąd połączenia:\n${result.error}`);
      }
    } catch (error) {
      alert(`❌ Błąd połączenia z API`);
    } finally {
      setTestingConnection(null);
    }
  };

  // Sprawdź status synchronizacji (WYŁĄCZONE - endpoint nie istnieje)
  const checkSyncStatus = async () => {
    // Endpoint /ai-settings/sync/check nie istnieje w backendzie
    // TO DO: Dodać endpoint lub usunąć tę funkcjonalność
    console.log('Sync check disabled - endpoint does not exist');
    setSyncStatus(null); // Ukryj panel synchronizacji
  };

  // Wymuś synchronizację (WYŁĄCZONE - endpoint nie istnieje)
  const forceSync = async () => {
    // Endpoint /ai-settings/sync/force nie istnieje w backendzie
    alert('ℹ️ Funkcja synchronizacji jest w trakcie rozwoju');
    return;
  };

  // Pobierz statystyki użycia
  const fetchUsageStats = async () => {
    setLoadingStats(true);
    try {
      // Pobierz statystyki dla wszystkich modułów
      const allStats = await Promise.all(
        modules.map(async (module) => {
          try {
            const response = await fetch(`${API_URL}/universal-ai/usage/${module.moduleName}?period=30d`);
            if (!response.ok) {
              // Jeśli endpoint nie istnieje, zwróć puste dane
              console.warn(`Usage stats not available for ${module.moduleName}`);
              return null;
            }
            const data = await response.json();
            return {
              moduleName: module.moduleName,
              displayName: module.displayName,
              ...data
            };
          } catch (error) {
            console.warn(`Error fetching stats for ${module.moduleName}:`, error);
            return null;
          }
        })
      );
      
      // Pobierz logi
      try {
        const logsResponse = await fetch(`${API_URL}/universal-ai/logs?limit=50`);
        if (logsResponse.ok) {
          const logs = await logsResponse.json();
          setUsageStats({
            modules: allStats.filter(s => s !== null),
            logs: Array.isArray(logs) ? logs : [],
            totalCost: modules.reduce((sum, m) => sum + m.currentUsage, 0),
            totalRequests: allStats.reduce((sum, s) => sum + (s?.summary?.total_requests || 0), 0)
          });
        } else {
          // Jeśli logi nie są dostępne, pokaż dane bez logów
          setUsageStats({
            modules: allStats.filter(s => s !== null),
            logs: [],
            totalCost: modules.reduce((sum, m) => sum + m.currentUsage, 0),
            totalRequests: 0
          });
        }
      } catch (error) {
        // Jeśli logi nie działają, kontynuuj bez nich
        console.warn('Logs endpoint not available');
        setUsageStats({
          modules: allStats.filter(s => s !== null),
          logs: [],
          totalCost: modules.reduce((sum, m) => sum + m.currentUsage, 0),
          totalRequests: 0
        });
      }
    } catch (error) {
      console.error('Error fetching usage stats:', error);
      // W przypadku błędu, pokaż puste statystyki
      setUsageStats({
        modules: [],
        logs: [],
        totalCost: 0,
        totalRequests: 0
      });
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchProviders();
    fetchModules();
    checkSyncStatus();
  }, []);
  
  useEffect(() => {
    if (activeTab === 'usage' && modules.length > 0) {
      fetchUsageStats();
    }
  }, [activeTab, modules]);

  // Renderowanie karty modułu
  const renderModuleCard = (module: ModuleConfig) => {
    const Icon = module.icon;
    const isEditing = editingModule?.moduleName === module.moduleName;
    const limitUsage = module.monthlyLimit > 0 
      ? (module.currentUsage / module.monthlyLimit) * 100 
      : 0;
    const isOverLimit = limitUsage >= 100;
    const isNearLimit = limitUsage >= 80;
    
    const currentProvider = providers.find(p => p.id === module.currentProvider);
    const currentModel = currentProvider?.models.find(m => m.id === module.currentModel);
    
    return (
      <div
        key={module.moduleName}
        className="bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10 p-6 hover:border-violet-500/30 transition-all animate-fadeIn"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-3 bg-gradient-to-br ${
              module.isEnabled ? 'from-violet-500/20 to-purple-500/20' : 'from-gray-500/20 to-gray-600/20'
            } rounded-xl`}>
              <Icon className={`w-6 h-6 ${module.isEnabled ? 'text-violet-400' : 'text-gray-400'}`} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{module.displayName}</h3>
              <p className="text-sm text-gray-400">{module.description}</p>
            </div>
          </div>
          <button
            onClick={() => setEditingModule(isEditing ? null : module)}
            className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-all"
          >
            <Settings className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Status i wykorzystanie */}
        <div className="space-y-3">
          {/* Status konfiguracji */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Status</span>
            <div className="flex items-center gap-2">
              {module.currentProvider === 'none' ? (
                <span className="text-sm text-gray-500">Nie skonfigurowano</span>
              ) : !module.hasKey ? (
                <span className="text-sm text-yellow-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Brak klucza API
                </span>
              ) : module.isEnabled ? (
                <span className="text-sm text-green-400 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Aktywny
                </span>
              ) : (
                <span className="text-sm text-red-400 flex items-center gap-1">
                  <X className="w-3 h-3" />
                  Wyłączony
                </span>
              )}
            </div>
          </div>

          {/* Model AI */}
          {module.currentProvider !== 'none' && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Model AI</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white">
                  {currentProvider?.name} / {currentModel?.name || 'Nie wybrano'}
                </span>
              </div>
            </div>
          )}

          {/* Wykorzystanie limitu */}
          {module.currentProvider !== 'none' && (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Wykorzystanie</span>
                <span className={`text-sm font-medium ${
                  isOverLimit ? 'text-red-400' : isNearLimit ? 'text-yellow-400' : 'text-green-400'
                }`}>
                  ${module.currentUsage.toFixed(2)} / ${module.monthlyLimit.toFixed(2)}
                </span>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    isOverLimit ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(100, limitUsage)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Rozwinięte ustawienia */}
        {isEditing && (
          <div className="mt-6 pt-6 border-t border-white/10 space-y-4 animate-slideDown">
            {/* Wybór dostawcy */}
            <div>
              <label className="text-sm font-medium text-gray-300 mb-2 block">Dostawca API</label>
              <select
                value={editingModule.currentProvider}
                onChange={(e) => {
                  const provider = e.target.value;
                  const firstModel = provider !== 'none' 
                    ? providers.find(p => p.id === provider)?.models[0]?.id || ''
                    : '';
                  setEditingModule({
                    ...editingModule,
                    currentProvider: provider,
                    currentModel: firstModel
                  });
                }}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-violet-500"
              >
                <option value="none">Brak (wyłączone)</option>
                {providers.map(provider => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Wybór modelu */}
            {editingModule.currentProvider !== 'none' && (
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Model</label>
                <div className="space-y-2">
                  {providers
                    .find(p => p.id === editingModule.currentProvider)
                    ?.models?.map(model => (
                      <button
                        key={model.id}
                        onClick={() => setEditingModule({
                          ...editingModule,
                          currentModel: model.id
                        })}
                        className={`w-full p-3 rounded-lg border transition-all text-left ${
                          editingModule.currentModel === model.id
                            ? 'bg-violet-600/20 border-violet-500 text-white'
                            : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium">{model.name}</div>
                            <div className="text-xs text-gray-400 mt-1">{model.description}</div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-sm font-bold text-green-400">
                              ${model.costPer1k}
                            </div>
                            <div className="text-xs text-gray-500">/ 1k tokenów</div>
                          </div>
                        </div>
                      </button>
                    )) || (
                      <div className="text-sm text-gray-500 text-center py-4">
                        Brak dostępnych modeli
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Klucz API */}
            {editingModule.currentProvider !== 'none' && (
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">
                  Klucz API
                  {providers.find(p => p.id === editingModule.currentProvider) && (
                    <a
                      href={providers.find(p => p.id === editingModule.currentProvider)?.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-violet-400 hover:text-violet-300"
                    >
                      <ExternalLink className="w-3 h-3 inline" />
                    </a>
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={editingModule.apiKey}
                    onChange={(e) => setEditingModule({
                      ...editingModule,
                      apiKey: e.target.value
                    })}
                    placeholder={providers.find(p => p.id === editingModule.currentProvider)?.keyFormat || 'Klucz API'}
                    className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  />
                  <button
                    onClick={() => testAPIConnection(editingModule)}
                    disabled={testingConnection === editingModule.moduleName}
                    className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    {testingConnection === editingModule.moduleName ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4" />
                    )}
                    Test
                  </button>
                </div>
              </div>
            )}

            {/* Limit miesięczny */}
            <div>
              <label className="text-sm font-medium text-gray-300 mb-2 block">Limit miesięczny ($)</label>
              <input
                type="number"
                value={editingModule.monthlyLimit}
                onChange={(e) => setEditingModule({
                  ...editingModule,
                  monthlyLimit: parseFloat(e.target.value) || 0
                })}
                step="10"
                min="0"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-violet-500"
              />
            </div>

            {/* Toggle aktywności */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-300">Moduł aktywny</span>
              <button
                onClick={() => setEditingModule({
                  ...editingModule,
                  isEnabled: !editingModule.isEnabled
                })}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  editingModule.isEnabled ? 'bg-violet-600' : 'bg-gray-600'
                }`}
              >
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  editingModule.isEnabled ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </button>
            </div>

            {/* Przyciski akcji */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => saveModuleSettings(editingModule)}
                disabled={isSaving}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Zapisz
              </button>
              <button
                onClick={() => setEditingModule(null)}
                className="px-4 py-2 bg-white/10 text-gray-400 rounded-lg hover:bg-white/20 transition-all"
              >
                Anuluj
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 p-6">
      {/* Header */}
      <div className="mb-8 animate-fadeIn">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">🤖 Centrum AI - Ustawienia API</h1>
            <p className="text-gray-400">Skonfiguruj różne modele AI dla każdego modułu systemu</p>
          </div>
          
          <button
            onClick={fetchModules}
            className="p-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all"
            disabled={isLoading}
          >
            <RefreshCw className={`w-5 h-5 text-violet-400 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { id: 'modules', label: 'Moduły', icon: Settings },
          { id: 'providers', label: 'Dostawcy AI', icon: Globe },
          { id: 'usage', label: 'Statystyki', icon: BarChart3 }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-violet-600 text-white'
                : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Status synchronizacji - NOWY PANEL */}
      {syncStatus && (
        <div className={`mb-6 p-4 rounded-xl border ${
          syncStatus.consistent 
            ? 'bg-green-500/10 border-green-500/30' 
            : 'bg-yellow-500/10 border-yellow-500/30'
        } animate-fadeIn`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className={`w-5 h-5 ${
                syncStatus.consistent ? 'text-green-400' : 'text-yellow-400'
              }`} />
              <div>
                <p className="text-sm font-medium text-white">
                  Status synchronizacji danych
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {syncStatus.consistent 
                    ? '✅ Dane są zsynchronizowane między Supabase i SQLite' 
                    : '⚠️ Wykryto różnice między bazami danych'}
                </p>
                {syncStatus.differences && (
                  <div className="mt-2 text-xs text-yellow-300">
                    Różnice w: {Object.entries(syncStatus.differences)
                      .filter(([_, isDiff]) => isDiff)
                      .map(([key]) => key)
                      .join(', ')}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={forceSync}
              disabled={syncingData}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {syncingData ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Synchronizuj
            </button>
          </div>
        </div>
      )}

      {/* Podsumowanie kosztów i alertów */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Całkowite koszty */}
        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 backdrop-blur-xl rounded-xl border border-green-500/30 p-4">
          <div className="flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-green-400" />
            <div>
              <p className="text-sm text-gray-400">Całkowite koszty (miesiąc)</p>
              <p className="text-2xl font-bold text-white">
                ${modules.reduce((sum, m) => sum + m.currentUsage, 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        
        {/* Alert o drogich modelach */}
        {modules.some(m => m.currentModel.includes('sonnet') || m.currentModel.includes('opus')) && (
          <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 backdrop-blur-xl rounded-xl border border-yellow-500/30 p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-sm text-yellow-400 font-medium">Drogie modele w użyciu!</p>
                <p className="text-xs text-gray-400 mt-1">
                  {modules.filter(m => m.currentModel.includes('sonnet') || m.currentModel.includes('opus')).map(m => m.displayName).join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Aktywne moduły */}
        <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 backdrop-blur-xl rounded-xl border border-violet-500/30 p-4">
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8 text-violet-400" />
            <div>
              <p className="text-sm text-gray-400">Aktywne moduły</p>
              <p className="text-2xl font-bold text-white">
                {modules.filter(m => m.isEnabled).length} / {modules.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'modules' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {isLoading ? (
            <div className="col-span-2 flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 text-violet-400 animate-spin" />
            </div>
          ) : (
            modules.map(module => renderModuleCard(module))
          )}
        </div>
      )}

      {activeTab === 'providers' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {providers.map(provider => (
            <div
              key={provider.id}
              className="bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10 p-6 hover:border-violet-500/30 transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">{provider.name}</h3>
                <a
                  href={provider.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-violet-600/20 text-violet-400 rounded-lg hover:bg-violet-600/30 transition-all"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              
              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-400">Format klucza:</p>
                <code className="text-xs bg-white/10 px-2 py-1 rounded text-violet-300">
                  {provider.keyFormat}
                </code>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-400 mb-2">Dostępne modele:</p>
                {provider.models.map(model => (
                  <div key={model.id} className="bg-white/5 p-2 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white font-medium">{model.name}</span>
                      <span className="text-xs text-green-400">${model.costPer1k}/1k</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{model.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'usage' && (
        <div className="space-y-6">
          {/* Podsumowanie statystyk */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-white/10 p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="w-6 h-6 text-green-400" />
                <div>
                  <p className="text-xs text-gray-400">Łączny koszt (30 dni)</p>
                  <p className="text-xl font-bold text-white">
                    ${usageStats?.totalCost?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-white/10 p-4">
              <div className="flex items-center gap-3">
                <Activity className="w-6 h-6 text-violet-400" />
                <div>
                  <p className="text-xs text-gray-400">Liczba zapytań</p>
                  <p className="text-xl font-bold text-white">
                    {usageStats?.totalRequests || 0}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-white/10 p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-blue-400" />
                <div>
                  <p className="text-xs text-gray-400">Średni koszt/zapytanie</p>
                  <p className="text-xl font-bold text-white">
                    ${usageStats?.totalRequests > 0 ? (usageStats.totalCost / usageStats.totalRequests).toFixed(4) : '0.00'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-white/10 p-4">
              <div className="flex items-center gap-3">
                <Zap className="w-6 h-6 text-yellow-400" />
                <div>
                  <p className="text-xs text-gray-400">Najaktywniejszy moduł</p>
                  <p className="text-xl font-bold text-white">
                    {usageStats?.modules?.sort((a: any, b: any) => 
                      (b.summary?.total_requests || 0) - (a.summary?.total_requests || 0)
                    )[0]?.displayName || '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Statystyki per moduł */}
          <div className="bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <h3 className="text-xl font-bold text-white mb-6">Statystyki według modułów</h3>
            
            {loadingStats ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-violet-400 animate-spin" />
              </div>
            ) : usageStats?.modules?.filter((m: any) => m.summary?.total_requests > 0).length > 0 ? (
              <div className="space-y-4">
                {usageStats.modules
                  .filter((m: any) => m.summary?.total_requests > 0)
                  .sort((a: any, b: any) => (b.summary?.total_cost || 0) - (a.summary?.total_cost || 0))
                  .map((module: any) => (
                    <div key={module.moduleName} className="bg-white/5 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-white">{module.displayName}</h4>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-green-400">
                            ${(module.summary?.total_cost || 0).toFixed(4)}
                          </span>
                          <span className="text-gray-400">
                            {module.summary?.total_requests || 0} zapytań
                          </span>
                          <span className="text-blue-400">
                            {module.summary?.avg_response_time ? 
                              `${Math.round(module.summary.avg_response_time)}ms` : '-'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Wykres dzienny */}
                      {module.daily && module.daily.length > 0 && (
                        <div className="mt-3 grid grid-cols-7 gap-1">
                          {module.daily.slice(0, 7).reverse().map((day: any, i: number) => (
                            <div key={i} className="text-center">
                              <div 
                                className="h-16 bg-violet-500/20 rounded relative overflow-hidden"
                                title={`${day.date}: ${day.requests} req, ${day.total_cost?.toFixed(4)}`}
                              >
                                <div 
                                  className="absolute bottom-0 left-0 right-0 bg-violet-500"
                                  style={{ 
                                    height: `${Math.min(100, (day.requests / Math.max(...module.daily.map((d: any) => d.requests))) * 100)}%` 
                                  }}
                                />
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(day.date).toLocaleDateString('pl-PL', { day: 'numeric', month: 'numeric' })}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-12">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Brak danych statystycznych - moduły nie były jeszcze używane</p>
              </div>
            )}
          </div>
          
          {/* Ostatnie logi */}
          {usageStats?.logs?.length > 0 && (
            <div className="bg-black/40 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
              <h3 className="text-xl font-bold text-white mb-6">Ostatnie użycia AI</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 text-gray-400">Czas</th>
                      <th className="text-left py-2 text-gray-400">Moduł</th>
                      <th className="text-left py-2 text-gray-400">Model</th>
                      <th className="text-right py-2 text-gray-400">Tokeny</th>
                      <th className="text-right py-2 text-gray-400">Koszt</th>
                      <th className="text-right py-2 text-gray-400">Czas odp.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageStats.logs.slice(0, 20).map((log: any, i: number) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="py-2 text-gray-300">
                          {new Date(log.created_at).toLocaleString('pl-PL', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </td>
                        <td className="py-2 text-white">{log.module_name}</td>
                        <td className="py-2 text-gray-300">{log.model_used}</td>
                        <td className="py-2 text-right text-blue-400">{log.tokens_used || 0}</td>
                        <td className="py-2 text-right text-green-400">
                          ${(log.cost || 0).toFixed(6)}
                        </td>
                        <td className="py-2 text-right text-gray-400">
                          {log.response_time ? `${Math.round(log.response_time)}ms` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="mt-8 p-4 bg-violet-500/10 border border-violet-500/30 rounded-xl animate-fadeIn">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-violet-400 mt-0.5" />
          <div>
            <p className="text-sm text-violet-300 font-medium mb-1">
              Jak to działa?
            </p>
            <ul className="text-xs text-violet-200/70 space-y-1">
              <li>• Każdy moduł może używać innego modelu AI</li>
              <li>• System automatycznie wybiera najtańszy model gdy zbliżasz się do limitu</li>
              <li>• Możesz w każdej chwili zmienić dostawcę bez utraty danych</li>
              <li>• Wszystkie rozmowy i dane są zapisywane lokalnie w bazie</li>
              <li>• Limity resetują się automatycznie co miesiąc</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APISettingsPage;
